import React, { FC, Fragment, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { partition, includes } from 'lodash';
import DiamondSvg from '@shared/assets/svgs/diamond.svg';
import EuroSvg from '@shared/assets/svgs/euro.svg';
import { CandidateProfileDataItem } from '@src/components/CandidateProfileDataItem';
import FrBinocularSvg from '@src/svgs/fr-binocular.svg';
import FrUserSvg from '@src/svgs/fr-user.svg';
import FrClipboardSvg from '@src/svgs/fr-clipboard.svg';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { useApplicationForCandidateProfileDataItemsQuery, useGetSalaryPeriodsQuery } from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { CandidateProfileDataItemsWithApplicationProps } from './props';
import '../styles.scss';

export const CandidateProfileDataItemsWithApplication: FC<CandidateProfileDataItemsWithApplicationProps> = ({
  className,
  applicationId,
  user,
}) => {
  const dispatch = useAppDispatch();

  const {
    loading: isLoading,
    error,
    data,
  } = useApplicationForCandidateProfileDataItemsQuery({
    onError: () => dispatch(setErrorFlashMessage()),
    variables: { id: applicationId },
  });

  const { data: salaryPeriodsData } = useGetSalaryPeriodsQuery();

  const salaryPeriods = salaryPeriodsData?.salaryPeriods;

  const mapNames = useCallback((list: { name: string }[]) => {
    return list.map((item, i) => {
      if (item.name.length) {
        return (
          <Fragment key={i}>
            {i !== 0 && ' or '}
            <strong>{item.name}</strong>
          </Fragment>
        );
      }

      return null;
    });
  }, []);

  const profileList = useMemo(
    () => ({
      haveFollowingSkills: 'And have following skills',
      isLookinFor: 'Is looking for',
      monthlySalaryIs: 'Expexted monthly salary is',
      seniorityIs: 'Who’s seniority is',
      specialityAre: 'Who’s speciality are',
    }),
    [],
  );

  if (isLoading || error) {
    return null;
  }

  const seniorities = data?.application.userMatchingProfile.seniorities;
  const profileSkills = data?.application.userMatchingProfile.skills;
  const speciality = data?.application.userMatchingProfile.speciality;
  const salary = user.profile.originalSalary;
  const specialityName = speciality?.name;
  const skillTags = speciality?.skillTags;
  const salaryPeriod = salary?.period;
  const salaryValue = salary?.value;
  const symbol = salary?.currency?.symbol;

  const specialityTag = skillTags?.[0];
  const salaryPeriodLabel = salaryPeriods?.find(({ value }) => value === salaryPeriod)?.label;
  const [specialities, skills] = partition(profileSkills, ({ tags }) => includes(tags, specialityTag));

  return (
    <div className={classnames('candidate-profile-data-items', className)}>
      {specialityName && (
        <CandidateProfileDataItem
          data={
            <Fragment>
              <strong>{specialityName}</strong> positions
            </Fragment>
          }
          icon={FrBinocularSvg}
          label={profileList.isLookinFor}
        />
      )}

      {!!specialities?.length && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(specialities)}
          icon={FrUserSvg}
          label={profileList.specialityAre}
        />
      )}

      {!!skills?.length && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(skills)}
          icon={DiamondSvg}
          label={profileList.haveFollowingSkills}
        />
      )}

      {!!seniorities?.length && (
        <CandidateProfileDataItem
          className="candidate-profile-data-items__item"
          data={mapNames(seniorities)}
          icon={FrClipboardSvg}
          label={profileList.seniorityIs}
        />
      )}

      <CandidateProfileDataItem
        className="candidate-profile-data-items__item"
        data={
          <strong>
            {' '}
            {symbol}
            {salaryValue}
          </strong>
        }
        icon={EuroSvg}
        label={`Expected ${salaryPeriodLabel} salary is`}
      />
    </div>
  );
};
